import React, { useMemo, useRef, useEffect, useState, useCallback } from "react";
import ZahlungsplanCell from "./ZahlungsplanCell";
import ZahlungsplanFooter from "./ZahlungsplanFooter";
import {
  generateWeeksForPhases,
  buildEntryMap,
  buildCellKey,
  formatDateSwiss,
  sumCellEntries,
} from "./zahlungsplanUtils";

const ZahlungsplanTable = ({
  phases,
  entries,
  columns,
  globalSettings,
  readOnly,
  expandedPhases,
  onTogglePhase,
  onSaveCell,
  onDeleteEntry,
  onDoubleClickCell,
  onContextMenu,
  // Partner search props
  addPartnerForPhase,
  onOpenPartnerSearch,
  partnerSearchValue,
  onPartnerSearchChange,
  partnerSuggestions,
  onSelectPartner,
  onCreateNewPartner,
  hideEmptyWeeks,
  selectedPlaner,
  incomeBudgets,
  onIncomeBudgetChange,
  editRequestId,
  onEditRequestHandled,
}) => {
  const currentWeekRef = useRef(null);
  const [dragOverCell, setDragOverCell] = useState(null);
  const [dragSource, setDragSource] = useState(null);
  const searchInputRef = useRef(null);

  // Generate week rows
  const allWeeks = useMemo(() => generateWeeksForPhases(phases, 8, entries), [phases, entries]);

  // Build entry lookup map
  const entryMap = useMemo(() => buildEntryMap(entries), [entries]);

  // Filter out empty weeks if hideEmptyWeeks is active
  const weeks = useMemo(() => {
    if (!hideEmptyWeeks) return allWeeks;
    return allWeeks.filter((week) => {
      if (week.isCurrentWeek) return true;
      const weekKey = `${week.year}-${week.number}`;
      return Object.keys(entryMap).some((key) => key.endsWith(`|${weekKey}`));
    });
  }, [allWeeks, hideEmptyWeeks, entryMap]);

  // Scroll to current week on mount
  useEffect(() => {
    if (currentWeekRef.current) {
      setTimeout(() => {
        currentWeekRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 200);
    }
  }, [weeks]);

  // Focus search input when partner search opens
  useEffect(() => {
    if (addPartnerForPhase && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [addPartnerForPhase]);

  // Drag & Drop handlers
  const handleDragStart = useCallback((e, entry, column) => {
    setDragSource({ entry, columnKey: column.key });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", entry.id);
  }, []);

  const handleDragOver = useCallback(
    (e, column, week) => {
      if (!dragSource) return;
      const sourceCol = columns.find((c) => c.key === dragSource.columnKey);
      if (!sourceCol) return;
      if (sourceCol.type !== column.type) return;
      if (sourceCol.phase.id !== column.phase.id && sourceCol.phase.name !== column.phase.name) return;
      if (sourceCol.partnerId !== column.partnerId) return;

      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOverCell(`${column.key}|${week.year}-${week.number}`);
    },
    [dragSource, columns]
  );

  const handleDrop = useCallback(
    (e, column, week) => {
      e.preventDefault();
      setDragOverCell(null);
      setDragSource(null);
      if (!dragSource) return;

      const sourceCol = columns.find((c) => c.key === dragSource.columnKey);
      if (!sourceCol) return;
      if (sourceCol.phase.id !== column.phase.id && sourceCol.phase.name !== column.phase.name) return;
      if (sourceCol.partnerId !== column.partnerId) return;

      onSaveCell(column, week, dragSource.entry.netto_betrag || dragSource.entry.betrag, dragSource.entry);
    },
    [dragSource, columns, onSaveCell]
  );

  const handleDragEnd = useCallback(() => {
    setDragOverCell(null);
    setDragSource(null);
  }, []);

  // Alle Einträge einer Zelle (Name- UND ID-Schlüssel, alte Einträge speichern
  // die Phasen-ID). Kann mehrere Einträge enthalten — die Zelle zeigt die Summe.
  const getEntriesForCell = (column, week) => {
    const weekKey = `${week.year}-${week.number}`;
    const phaseName = column.phase?.name || "";
    const phaseId = column.phase?.id || "";

    let byName = [];
    let byId = [];
    if (column.type === "einnahmen") {
      byName = entryMap[buildCellKey("Einnahme", phaseName, weekKey)] || [];
      byId = phaseId !== phaseName
        ? entryMap[buildCellKey("Einnahme", phaseId, weekKey)] || []
        : [];
    } else if (column.type === "ausgaben") {
      byName = entryMap[buildCellKey("Ausgabe", phaseName, weekKey, column.partnerId)] || [];
      byId = phaseId !== phaseName
        ? entryMap[buildCellKey("Ausgabe", phaseId, weekKey, column.partnerId)] || []
        : [];
    }
    return [...byName, ...byId];
  };

  return (
    <div
      className="zp-table-container"
      onDragEnd={handleDragEnd}
      onClick={() => { if (addPartnerForPhase) onOpenPartnerSearch(null); }}
    >
      <table className="zp-table">
        <thead>
          <tr className="zp-header-row">
            <th className="zp-th zp-th-kw">KW</th>
            <th className="zp-th zp-th-date">Datum</th>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`zp-th zp-th-data ${col.type} ${col.type === "einnahmen" && addPartnerForPhase === col.phaseKey ? "has-dropdown" : ""}`}
                title={col.label}
              >
                <div className="zp-th-content">
                  <span className="zp-col-label">{col.shortLabel || col.label}</span>
                  {col.type === "einnahmen" && (
                    <>
                      <button
                        className={`zp-expand-toggle ${expandedPhases.has(col.phaseKey) ? "expanded" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onTogglePhase(col.phaseKey);
                        }}
                        title={expandedPhases.has(col.phaseKey) ? "Ausgaben ausblenden" : "Ausgaben einblenden"}
                      >
                        {expandedPhases.has(col.phaseKey) ? "◀" : "▶"}
                      </button>
                      {expandedPhases.has(col.phaseKey) && !readOnly && (
                        <button
                          className="zp-add-partner-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenPartnerSearch(
                              addPartnerForPhase === col.phaseKey ? null : col.phaseKey
                            );
                            onPartnerSearchChange("");
                          }}
                          title="Partner/Ausgaben-Spalte hinzufügen"
                        >
                          +
                        </button>
                      )}
                    </>
                  )}
                </div>
                {col.type === "einnahmen" && (
                  <span className="zp-col-type-label einnahmen">Einnahme</span>
                )}
                {col.type === "ausgaben" && (
                  <span className="zp-col-type-label ausgaben">Ausgabe</span>
                )}
                {/* Partner search dropdown - appears under einnahmen header */}
                {col.type === "einnahmen" && addPartnerForPhase === col.phaseKey && (
                  <div className="zp-partner-search-dropdown">
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="zp-partner-search-input"
                      placeholder="Partner suchen..."
                      value={partnerSearchValue}
                      onChange={(e) => onPartnerSearchChange(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          onOpenPartnerSearch(null);
                        }
                      }}
                    />
                    {partnerSuggestions.length > 0 && (
                      <ul className="zp-partner-suggestions">
                        {partnerSuggestions.map((p) => (
                          <li
                            key={p.id}
                            className="zp-partner-suggestion"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectPartner(p, col.phaseKey);
                            }}
                          >
                            <span className="zp-partner-name">{p.name}</span>
                            {p.role && <span className="zp-partner-role">{p.role}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                    {partnerSearchValue.length >= 2 && partnerSuggestions.length === 0 && (
                      <div className="zp-partner-no-results">
                        <span>Kein Partner gefunden</span>
                        <button
                          className="zp-partner-create-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCreateNewPartner(col.phaseKey);
                          }}
                        >
                          "{partnerSearchValue}" erstellen
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => {
            const weekKey = `${week.year}-${week.number}`;
            return (
              <tr
                key={weekKey}
                className={`zp-row ${week.isCurrentWeek ? "current-week" : ""}`}
                ref={week.isCurrentWeek ? currentWeekRef : null}
              >
                <td className="zp-td zp-td-kw">KW {week.number}</td>
                <td className="zp-td zp-td-date">{formatDateSwiss(week.startDate)}</td>
                {columns.map((col) => {
                  const cellKey = `${col.key}|${weekKey}`;
                  const cellEntries = getEntriesForCell(col, week);
                  const cellEntry = cellEntries[0] || null;
                  return (
                    <td key={cellKey} className={`zp-td zp-td-data ${col.type}`}>
                      <ZahlungsplanCell
                        entry={cellEntry}
                        entryCount={cellEntries.length}
                        cellTotal={sumCellEntries(cellEntries)}
                        column={col}
                        week={week}
                        readOnly={readOnly}
                        onSave={onSaveCell}
                        onDelete={onDeleteEntry}
                        onDoubleClick={onDoubleClickCell}
                        onContextMenu={onContextMenu}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        isDragOver={dragOverCell === cellKey}
                        isEditRequested={!!cellEntry && cellEntry.id === editRequestId}
                        onEditRequestHandled={onEditRequestHandled}
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <ZahlungsplanFooter
            columns={columns}
            entries={entries}
            phases={phases}
            globalSettings={globalSettings}
            selectedPlaner={selectedPlaner}
            incomeBudgets={incomeBudgets}
            onIncomeBudgetChange={onIncomeBudgetChange}
          />
        </tfoot>
      </table>
    </div>
  );
};

export default ZahlungsplanTable;
